import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useTranslation } from 'react-i18next';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import ConfirmIcon from 'components/ConfirmIcon';
import FileUploadList from 'components/FileUploadList';
import MailPopUp from 'components/MailPopUp';
import UploadButton from 'components/UploadButton';
import useTypedForm from 'components/common/Form/components';
import { Breadcrumb } from 'components/Breadcrumbs/Breadcrumb';
import { IDocument } from '../../../components/myItIdea/components/DocumentRow';
import { useAppContext } from 'AppContext';

import {
  GetMasterWithApprovalsDocument,
  useCreateProjectDocumentMutation,
  useGetMailInfoByProjectLazyQuery,
  useGetProjectDocumentsLazyQuery,
  useRemoveProjectDocumentMutation,
  useUpdateApprovalStatusMutation,
} from 'graphql/codegen/graphql';
import config, { getFrontendUrl } from 'config/config';

export interface ApprovalGeneralWorksCouncilProps {
  approved: boolean;
  confirmed: boolean;
  projectId: string;
}

type Mail = {
  cc?: string;
  subject: string;
  body: string;
};

const ApprovalGeneralWorksCouncil = ({
  approved,
  confirmed,
  projectId,
}: ApprovalGeneralWorksCouncilProps) => {
  const { Form, IconSubmitButton } = useTypedForm();
  const { user } = useAppContext();

  const [getProjectDocuments, { data: projectDocumentsData }] =
    useGetProjectDocumentsLazyQuery();
  const [updateApprovalStatusMutation] = useUpdateApprovalStatusMutation();
  const [createProjectDocumentMutation] = useCreateProjectDocumentMutation();
  const [removeProjectDocumentMutation] = useRemoveProjectDocumentMutation();
  const [getMailInfo, { data: mailInfoData }] =
    useGetMailInfoByProjectLazyQuery();

  const [docs, setDocs] = useState<(IDocument & { uri?: string })[]>(
    [] || ([] as any),
  );

  const loadProjectDocuments = async () => {
    return await getProjectDocuments({
      variables: {
        where: {
          projectId: {
            equals: projectId,
          },
          location: {
            equals: 'gbr',
          },
        },
      },
    });
  };

  const [mail, setMail] = useState<Mail>({
    cc: '',
    subject: 'initial',
    body: '',
  });

  useEffect(() => {
    loadContent();
    loadMailInfo();
  }, [mailInfoData, docs]);

  const loadMailInfo = () => {
    if (
      !mailInfoData?.employeeMasters[0] ||
      !mailInfoData.master.projectDescriptions![0]
    ) {
      const body: string =
        'Lieber Gesamtbetriebsrat,\n\nanbei finden Sie den Link zur HAW GBR-Erstanzeige für folgendes Projekt:\nProjektname: ' +
        '<hier Projektname einfügen>' +
        '\nProjektleiter: <hier Projektleiter einfügen>' +
        '\nLink:\n<hier Link einfügen>' +
        '\n\nViele Grüße\n ' +
        user?.givenName +
        ' ' +
        user?.surname;
      setMail({
        subject:
          '[Gesamtbetriebsrat] - IT-Projekt: <hier Projektname einfügen>',
        body,
      });
      return;
    }

    const cc: string = mailInfoData?.employeeMasters[0].employee.email || '';
    const subject: string = `[Gesamtbetriebsrat] - IT-Projekt: ${
      mailInfoData?.master.projectDescriptions![0].name
    }`;

    let body: string =
      'Lieber Gesamtbetriebsrat,\n\nanbei finden Sie den Link zur HAW GBR-Erstanzeige für folgendes Projekt:\nProjektname: ' +
      mailInfoData?.master.projectDescriptions![0].name +
      '\nProjektleiter: ';
    const projectLead: string =
      (mailInfoData?.employeeMasters[0].employee.firstName || '') +
      ' ' +
      (mailInfoData?.employeeMasters[0].employee.lastName || '');
    body = body + projectLead;
    docs.forEach((d, index) => {
      if (index === 0) {
        if (docs.length > 1) {
          body = body + '\nLinks:';
        } else {
          body = body + '\nLink:';
        }
      }
      body =
        body +
        '\n' +
        getFrontendUrl(
          `${config.localRoutes.dashboard.detail.replace(
            ':projectId',
            projectId,
          )}?download=${d.uri}`,
        );
    });
    body =
      body +
      '\n\nDu bekommst zusätzlich eine Approvalmail zur Genehmigung des Projektes.';
    body =
      body + '\nBitte genehmige diese nach der Prüfung des Projektes im GBR.\n';
    body = body + '\n\nViele Grüße\n' + user?.givenName + ' ' + user?.surname;

    setMail({
      cc,
      subject,
      body,
    });
  };

  const loadContent = () => {
    getMailInfo({
      variables: {
        where: {
          id: projectId,
        },
        where2: {
          projectId: {
            equals: projectId,
          },
        },
      },
    });

    loadProjectDocuments().then(response => {
      const documents = response && response.data!.projectDocuments!;
      if (documents.length !== 0) {
        setDocs(documents);
      }
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      loadProjectDocuments();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setDocs(projectDocumentsData?.projectDocuments ?? []);
  }, [projectDocumentsData]);

  const handleUpload = (files: FileList | null) => {
    const newDocs = [...docs];

    if (files?.length) {
      Array.from(files).map(f => {
        const doesExist = newDocs.some(n => n.name === f.name);
        if (!doesExist) {
          newDocs.push({
            id: uuidv4(),
            description: f.name,
            file: f,
          });
        }
      });

      newDocs.map(async (doc, index) => {
        if (doc.file) {
          await createProjectDocumentMutation({
            variables: {
              data: {
                id: doc.id,
                name: doc.file!.name,
                description: doc.description,
                location: 'gbr',
                uri: 'https://www.dummy-url.com',
                createdBy: user!.kid!,
                project: {
                  connect: {
                    id: projectId,
                  },
                },
              },
              file: doc.file,
            },
          }).then(res => {
            const allDocs = [...docs];
            allDocs.push({
              id: doc.id,
              description: doc.description,
              file: doc.file,
              uri: res.data?.createProjectDocument.uri,
            });
            setDocs(allDocs);
          });
        }
      });
    }
  };

  const handleDelete = async doc => {
    await removeProjectDocumentMutation({
      variables: {
        where: {
          id: doc.id,
        },
      },
    });
    setDocs(docs.filter(f => f.id !== doc.id));
  };

  const handleSubmit = () => {
    updateApprovalStatusMutation({
      variables: {
        data: {
          projectApproval: {
            create: [
              {
                createdBy: user!.kid!,
                ApprovalBoard: { connect: { id: 'gbr' } },
                approvalStatus:
                  process.env.NODE_ENV === 'development'
                    ? { connect: { id: 'angenommen' } }
                    : undefined,
              },
            ],
          },
        },
        projectId,
      },
      refetchQueries: ['getProjectDocuments', GetMasterWithApprovalsDocument],
    }).then(() => {
      loadContent();
    });
  };

  const { t } = useTranslation();
  return (
    <>
      <Breadcrumb
        label={t`myItProject.steps.gbr`}
        route={`/meine-it-initiative/${projectId}/gbr`}
      />
      <Form onSubmit={handleSubmit}>
        <div className="space-y-12">
          <p className="text-xl font-medium">{t`myItProject.generalWorksCouncil.description`}</p>
          <p className="text-xl font-medium">
            <Trans
              i18nKey={'myItProject.generalWorksCouncil.description2'}
              components={{
                href: (
                  <a
                    href="https://connect.eon.com/pages/pg-haw-it-themen/apps/wiki/einbindung-br-dsb-isms/list/view/ea2be170-14b8-4d3f-a459-ae81d07618c1#"
                    className="underline"
                  />
                ),
              }}
            />
          </p>
          <div className="flex flex-wrap">
            <div className="w-1/2">
              <UploadButton
                onUpload={handleUpload}
                multiple={false}
                disabled={docs.length > 0 || confirmed}
                theme="secondary"
              >
                <div className="space-x-2">
                  <FontAwesomeIcon icon={faUpload} />
                  <span>
                    {t('myItProject.generalWorksCouncil.upload_button')}
                  </span>
                </div>
              </UploadButton>
            </div>
            <div className="w-1/2">
              <FileUploadList
                files={docs}
                onDelete={handleDelete}
                disabled={confirmed}
              >
                <span className="font-semibold">
                  {t('myItProject.generalWorksCouncil.upload_button')}
                </span>
              </FileUploadList>
            </div>
          </div>
          <MailPopUp
            docs={docs}
            cc={mail.cc}
            subject={mail.subject}
            body={mail.body}
            projectId={projectId}
            approverId="gbr"
            isDisabled={docs.length === 0 || confirmed}
          ></MailPopUp>
          <p className="text-xl font-medium">
            {t('myItProject.generalWorksCouncil.description4')}
          </p>
          <div className="flex items-center space-x-5">
            <IconSubmitButton
              active
              disabled={docs.length === 0}
              approved={confirmed}
            >{t`myItProject.generalWorksCouncil.confirmedLabel`}</IconSubmitButton>
          </div>
          <p className="text-xl font-medium">{t`myItProject.generalWorksCouncil.description3`}</p>
          <ConfirmIcon
            enabled
            active={approved}
          >{t`myItProject.generalWorksCouncil.approvedLabel`}</ConfirmIcon>
        </div>
      </Form>
    </>
  );
};
export default ApprovalGeneralWorksCouncil;
